import { cn } from '@wellesley/ui'
import { memo, MouseEvent } from 'react'
import LinkPreview from 'components/LinkPreview'
import { AppsLink } from '@wellesley/router'
import { useCurrentUser } from '@wellesley/user'

import isQuote from '../helpers/isQuote'
import isRepost from '../helpers/isRepost'
import { Footer } from '../Footer'
import RepostedLabel from '../RepostedLabel'
import RepostedContent from '../RepostedContent'
import { MediaContent } from '../MediaContent'
import { getFeedPostRoute } from '../helpers/getFeedPostRoute'
import { PrivacyOptionsVariant } from '../../PostForm/types'
import preventPropagationForPost from '../helpers/preventPropagationForPost'
import getIsNotAvailablePostContent from '../helpers/getIsNotAvailablePostContent'

import FeedHeader from './FeedHeader'
import { FeedProps } from './types'
import FeedTextSection from './FeedTextSection'
import FeedPollSection from './FeedPollSection'
import PostLocation from './PostLocation'
import getFeedChannelInfo from './helpers/getFeedChannelInfo'
import getTimelineFeedProps from './helpers/getTimelineFeedProps'

const TimelineFeed = memo<FeedProps>((props: FeedProps) => {
  const {
    containerWidth,
    className,
    isQuotePost,
    isFooter = true,
    isActions = true,
    isFeedPage = false,
    showPinAction = true,
    isActivePostLink = true,
    isSmallImagePreview,
    hidePrivatePosts,
    renderUserWrapper,
    renderEditModal,
    groupName,
    channel,
    userMentionSearchScope,
    privacyOptionsVariant,
    children = (
      <div className="mt-1 h-[1px] w-full border-b border-dashed border-gray-200" />
    ),
    redirectOnShowMore = false,
    onOpenReplyInput,
    openedReplyInputId,
    ...other
  } = props
  const { user: currentProfile } = useCurrentUser()

  const feed = getTimelineFeedProps(other)

  const { text, summary, uploads, author } = feed

  const isQuoteFeed = isQuote(feed)
  const isRepostFeed = isRepost(feed)
  const isFeedAuthor = author.id === currentProfile?.id
  const isNotAvailableContent = getIsNotAvailablePostContent(
    feed,
    isQuotePost,
    hidePrivatePosts,
  )

  const isPostActive = isActivePostLink && !isNotAvailableContent
  const postRoute = getFeedPostRoute({ feed, groupName, channel })

  const wrapperClass = cn(
    'relative flex w-full max-w-[560px] flex-col gap-xs pt-s',
    className,
  )

  const isRepostedContent = isQuoteFeed && feed.originalPost

  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const selected = window.getSelection()?.toString().trim() ?? ''
    if (selected || !isPostActive) {
      e.preventDefault()
      e.stopPropagation()

      return true
    }
  }

  const { feedChannel, isChannelPublic } = getFeedChannelInfo(feed)
  const feedPrivacyOptionsVariant = feedChannel
    ? isChannelPublic
      ? PrivacyOptionsVariant.GroupChannelPublicPost
      : PrivacyOptionsVariant.GroupChannelPrivatePost
    : privacyOptionsVariant

  return (
    <div className={wrapperClass} data-testid={`timeline-feed-${feed.id}`}>
      {isRepostFeed && !isNotAvailableContent && (
        <RepostedLabel {...{ renderUserWrapper, feed }} />
      )}
      <AppsLink
        to={postRoute}
        data-testid="feed-link"
        className={cn('relative flex items-center gap-2', {
          'cursor-pointer': isPostActive,
        })}
        draggable={false}
        onClick={handleLinkClick}
      >
        <div
          className="flex w-full flex-1 flex-col"
          onClick={preventPropagationForPost}
        >
          <FeedHeader
            {...{ ...props, ...feed, renderEditModal, groupName, channel }}
            isVisiblePinAction={!isQuotePost && showPinAction}
            isActions={isActions}
            isFeedAuthor={isFeedAuthor}
            isNotAvailableContent={isNotAvailableContent}
            renderFeed={(props: FeedProps) => <TimelineFeed {...props} />}
            privacyOptionsVariant={feedPrivacyOptionsVariant}
          />
          {!isNotAvailableContent && text && (
            <FeedTextSection
              feed={feed}
              showFullText={isFeedPage}
              redirectOnShowMore={redirectOnShowMore}
              groupName={groupName}
              channel={channel}
              containerWidth={containerWidth}
            />
          )}
          {!isNotAvailableContent && feed.address && (
            <PostLocation address={feed.address} />
          )}
        </div>
      </AppsLink>
      {!isNotAvailableContent && uploads.length > 0 && (
        <MediaContent
          attachments={uploads}
          summary={summary}
          isReply={feed.path.length > 0}
          isSmallPreview={isSmallImagePreview}
          containerWidth={containerWidth}
        />
      )}
      {feed?.poll && <FeedPollSection feed={feed} />}
      {!isNotAvailableContent && feed?.card && (
        <LinkPreview card={feed?.card} showImage={uploads.length === 0} />
      )}
      {isRepostedContent && (
        <RepostedContent
          renderFeed={(props: FeedProps) => <TimelineFeed {...props} />}
          {...{
            feed,
            isFeedPage,
            renderUserWrapper,
            hidePrivatePosts,
            renderEditModal,
          }}
          canPinPost={props.canPinPost}
        />
      )}
      {isFooter && (
        <Footer
          {...{ feed, renderUserWrapper, isFeedAuthor }}
          userMentionSearchScope={userMentionSearchScope}
          isCommentForm={!isFeedPage}
          postId={feed.id}
          onOpenReplyInput={onOpenReplyInput}
          openedReplyInputId={openedReplyInputId}
          renderFeed={(props: FeedProps) => <TimelineFeed {...props} />}
          isDisabledCommentOption={false}
          privacyOptionsVariant={feedPrivacyOptionsVariant}
        />
      )}
      {children}
    </div>
  )
})

TimelineFeed.displayName = 'TimelineFeed'

export { TimelineFeed }
